import React, { useEffect, useMemo, useRef, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';

import type { Offer } from '@luxuryescapes/contract-svc-offer';

import { useDeleteOfferBuilderRoom } from '~/hooks/apis/accommodation/useDeleteOfferBuilderRoom';
import {
  type SavedOfferBuilderRoom,
  useSaveOfferBuilderRoom,
} from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import { useGetVendorOffers } from '~/hooks/apis/offers/useGetVendorOffers';

import getErrorMessage from '~/utils/getErrorMessage';

import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import ImageEditor from '../ImagesVideo/ImageEditor';
import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';
import { getPackageRoomRateId, getPackageRoomTypeId } from '../OfferPackages/utils';
import type { OfferBuilderMediaController } from '../useOfferBuilderMedia';

import AddRoomDialog from './AddRoomDialog';
import RoomAccordion from './RoomAccordion';
import type { SyncRoomPolicyParams } from './RoomTabs/PoliciesTab';
import { DEFAULT_ROOM_TAB } from './types';
import type { OfferBuilderRoom, RoomTab } from './types';
import { formatBedType, getMissingRequiredRoomFields, getRoomImageCount, getRoomKey, getRoomSummary } from './utils';

interface Props {
  isLoadingProperty?: boolean;
  media: OfferBuilderMediaController;
  onOpenImagesVideo: () => void;
  offerPackages?: Array<App.Package>;
  propertyError?: unknown;
  propertyId?: string;
  propertyName: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  vendorId?: string;
}

type DraftRoom = OfferBuilderRoom & {
  roomPolicyId?: number | null;
  roomKey: string;
  roomSummary: string;
  roomTitle: string;
};

interface AddRoomValues {
  bedType: string;
  category: string;
  description: string;
  name: string;
}

type OfferPackageWithRoomPolicy = App.Package & {
  fk_room_policy_id?: number | null;
  room_policy_description?: string | null;
};

const PACKAGE_ROOM_DELETE_WARNING = 'This room is part of a package. Remove it from packages before deleting it.';

function buildDraftRooms(propertyRoomTypes: Array<PropertyRoomTypeWithImages>): Array<DraftRoom> {
  return (propertyRoomTypes as Array<OfferBuilderRoom>).map((room, index) => ({
    ...room,
    room_policies: room.room_policies ?? '',
    roomPolicyId: null,
    roomKey: getRoomKey(room, index),
    roomSummary: getRoomSummary(room),
    roomTitle: room.name || 'Unnamed room',
  }));
}

function createDraftRoomKey() {
  return `new-room-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getAccommodationPackageOptions(offerPackage: App.Package) {
  return 'package_options' in offerPackage
    ? (offerPackage.package_options as Array<Offer.PackageOption> | undefined)
    : undefined;
}

function getPackageSalesforceId(offerPackage: App.Package) {
  return 'id_salesforce_external' in offerPackage ? offerPackage.id_salesforce_external : undefined;
}

function getRoomRateIds(room: OfferBuilderRoom) {
  const roomRateIds = room.room_rates?.map((roomRate) => roomRate.id).filter(Boolean) ?? [];

  return roomRateIds.length > 0 ? roomRateIds : undefined;
}

function getPackageRoomPolicyDescription(offerPackage?: OfferPackageWithRoomPolicy) {
  return offerPackage?.room_policy_description ?? undefined;
}

function getPackageRoomPolicyId(offerPackage?: OfferPackageWithRoomPolicy) {
  return offerPackage?.fk_room_policy_id ?? undefined;
}

function findMatchingPackageForRoom(room: OfferBuilderRoom, offerPackages: Array<App.Package>) {
  return offerPackages.find((offerPackage) => getPackageRoomTypeId(offerPackage) === room.id);
}

function isPackageAttachedToRoom(room: OfferBuilderRoom, offerPackage: App.Package) {
  if (!room.id) {
    return false;
  }

  return (
    getPackageRoomTypeId(offerPackage) === room.id ||
    !!getAccommodationPackageOptions(offerPackage)?.some((option) => option.fk_room_type_id === room.id)
  );
}

function isRoomAttachedToPackages(room: OfferBuilderRoom, offerPackages: Array<App.Package>) {
  return offerPackages.some((offerPackage) => isPackageAttachedToRoom(room, offerPackage));
}

function findOfferUsingRoom(room: OfferBuilderRoom, offers: Array<App.Offer>) {
  return offers.find((offer) => isRoomAttachedToPackages(room, offer.packages ?? []));
}

function getSyncRoomPolicyParams(room: OfferBuilderRoom, offerPackage?: App.Package): SyncRoomPolicyParams | undefined {
  if (!offerPackage) {
    return undefined;
  }

  const dealOfferId = getPackageSalesforceId(offerPackage);
  const propertyId = ('fk_property_id' in offerPackage ? offerPackage.fk_property_id : undefined) ?? room.property_id;
  const roomTypeId = getPackageRoomTypeId(offerPackage) ?? room.id;
  const roomRateId = getPackageRoomRateId(offerPackage);

  if (!dealOfferId || !propertyId || !roomTypeId || !roomRateId) {
    return undefined;
  }

  return {
    dealOfferId,
    propertyId,
    roomTypeId,
    roomRateId,
    roomRateIds: getRoomRateIds(room),
  };
}

export default function OfferBuilderRooms({
  isLoadingProperty,
  media,
  onOpenImagesVideo,
  offerPackages = [],
  propertyError,
  propertyId,
  propertyName,
  propertyRoomTypes = [],
  vendorId,
}: Props) {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [expandedRoomKeys, setExpandedRoomKeys] = useState<Array<string>>([]);
  const [activeRoomTabs, setActiveRoomTabs] = useState<Record<string, RoomTab>>({});
  const [searchText, setSearchText] = useState('');
  const [isAddRoomDialogOpen, setIsAddRoomDialogOpen] = useState(false);
  const [roomPendingDeletion, setRoomPendingDeletion] = useState<DraftRoom>();
  const [draftRoomTypes, setDraftRoomTypes] = useState<Array<DraftRoom>>([]);
  const [savingRoomKey, setSavingRoomKey] = useState<string>();
  const [isSavingAllRooms, setIsSavingAllRooms] = useState(false);
  const [saveAllRoomCount, setSaveAllRoomCount] = useState(-1);
  const [hasSaveAllRoomFailure, setHasSaveAllRoomFailure] = useState(false);
  const saveRoomMutation = useSaveOfferBuilderRoom();
  const deleteRoomMutation = useDeleteOfferBuilderRoom();
  const {
    data: vendorOffers,
    error: vendorOffersError,
    refetch: refetchVendorOffers,
  } = useGetVendorOffers({ enabled: false, vendorId });
  const [deletingRoomKey, setDeletingRoomKey] = useState<string>();
  const [isCheckingRoomUsage, setIsCheckingRoomUsage] = useState(false);
  const hasUserEditedRoomsRef = useRef(false);
  const lastSyncedRoomSourceRef = useRef('');

  useEffect(() => {
    const roomSourceKey = `${propertyName}:${propertyRoomTypes
      .map((room, index) => {
        const offerBuilderRoom = room as OfferBuilderRoom;
        return `${getRoomKey(room, index)}:${room.name || ''}:${offerBuilderRoom.description || ''}:${
          offerBuilderRoom.room_policies || ''
        }:${getRoomRateIds(offerBuilderRoom)?.join(',') || ''}`;
      })
      .join('|')}`;

    if (hasUserEditedRoomsRef.current && lastSyncedRoomSourceRef.current.startsWith(`${propertyName}:`)) {
      return;
    }

    if (lastSyncedRoomSourceRef.current === roomSourceKey) {
      return;
    }

    hasUserEditedRoomsRef.current = false;
    lastSyncedRoomSourceRef.current = roomSourceKey;
    setDraftRoomTypes(buildDraftRooms(propertyRoomTypes));
  }, [propertyName, propertyRoomTypes]);

  useEffect(() => {
    if (hasUserEditedRoomsRef.current || draftRoomTypes.length === 0 || offerPackages.length === 0) {
      return;
    }

    setDraftRoomTypes((currentRoomTypes) => {
      let hasRoomPolicyUpdate = false;

      const nextRoomTypes = currentRoomTypes.map((room) => {
        if (room.room_policies) {
          return room;
        }

        const packageRoomPolicyDescription = getPackageRoomPolicyDescription(
          findMatchingPackageForRoom(room, offerPackages),
        );

        if (!packageRoomPolicyDescription) {
          return room;
        }

        hasRoomPolicyUpdate = true;
        return { ...room, room_policies: packageRoomPolicyDescription };
      });

      return hasRoomPolicyUpdate ? nextRoomTypes : currentRoomTypes;
    });
  }, [draftRoomTypes.length, offerPackages]);

  const filteredRooms = useMemo(() => {
    const normalisedSearchText = searchText.trim().toLowerCase();

    if (!normalisedSearchText) {
      return draftRoomTypes;
    }

    return draftRoomTypes.filter((room) =>
      [room.name, room.category, room.description].some((value) => value?.toLowerCase().includes(normalisedSearchText)),
    );
  }, [draftRoomTypes, searchText]);

  const areAllRoomsExpanded = draftRoomTypes.length > 0 && expandedRoomKeys.length === draftRoomTypes.length;

  const updateRoom = (roomKey: string, nextRoomValues: Partial<OfferBuilderRoom>) => {
    hasUserEditedRoomsRef.current = true;
    setSaveAllRoomCount(-1);
    setDraftRoomTypes((currentRoomTypes) =>
      currentRoomTypes.map((room) => {
        if (room.roomKey !== roomKey) {
          return room;
        }

        const nextRoom = {
          ...room,
          // Keep the header summary stable while editing fields that affect its text.
          roomSummary: room.roomSummary,
          ...nextRoomValues,
        };

        return nextRoomValues.name !== undefined
          ? { ...nextRoom, roomTitle: nextRoom.name || (room.id ? 'Unnamed room' : 'New room') }
          : nextRoom;
      }),
    );
  };

  const addRoom = ({ bedType, category, description, name }: AddRoomValues) => {
    const roomKey = createDraftRoomKey();
    const roomPropertyId = propertyId ?? draftRoomTypes.find((room) => room.property_id)?.property_id;
    const newRoom = {
      amenities: [],
      bed_groups: [
        {
          id: 'main-bedroom',
          configuration: [{ type: bedType, size: '', quantity: 1 }],
        },
      ],
      category: category || null,
      description,
      images: [],
      name,
      number_of_beds: [1],
      property_id: roomPropertyId,
      roomKey,
      roomPolicyId: null,
      roomSummary: `${formatBedType(bedType)} Bed`,
      roomTitle: name,
      source: 'admin',
    } as DraftRoom;

    hasUserEditedRoomsRef.current = true;
    setDraftRoomTypes((currentRoomTypes) => currentRoomTypes.concat(newRoom));
    setExpandedRoomKeys((currentRoomKeys) => Array.from(new Set([roomKey, ...currentRoomKeys])));
    setActiveRoomTabs((currentTabs) => ({ ...currentTabs, [roomKey]: DEFAULT_ROOM_TAB }));
    setIsAddRoomDialogOpen(false);
  };

  const duplicateRoom = (roomToDuplicate: DraftRoom) => {
    const roomKey = createDraftRoomKey();
    const roomName = `${roomToDuplicate.name || roomToDuplicate.roomTitle || 'Room'} copy`;
    const duplicatedRoom = {
      ...cloneDeep(roomToDuplicate),
      id: undefined,
      images: [],
      name: roomName,
      roomKey,
      roomPolicyId: null,
      roomSummary: roomToDuplicate.roomSummary,
      roomTitle: roomName,
      source: 'admin',
    } as DraftRoom;

    hasUserEditedRoomsRef.current = true;
    setDraftRoomTypes((currentRoomTypes) => {
      const roomIndex = currentRoomTypes.findIndex((room) => room.roomKey === roomToDuplicate.roomKey);

      if (roomIndex === -1) {
        return currentRoomTypes.concat(duplicatedRoom);
      }

      return currentRoomTypes.slice(0, roomIndex + 1).concat(duplicatedRoom, currentRoomTypes.slice(roomIndex + 1));
    });
    setExpandedRoomKeys((currentRoomKeys) => Array.from(new Set([roomKey, ...currentRoomKeys])));
    setActiveRoomTabs((currentTabs) => ({
      ...currentTabs,
      [roomKey]: currentTabs[roomToDuplicate.roomKey] ?? DEFAULT_ROOM_TAB,
    }));
  };

  const removeDraftRoom = (roomKey: string) => {
    setDraftRoomTypes((currentRoomTypes) => currentRoomTypes.filter((room) => room.roomKey !== roomKey));
    setExpandedRoomKeys((currentRoomKeys) => currentRoomKeys.filter((currentRoomKey) => currentRoomKey !== roomKey));
    setActiveRoomTabs((currentTabs) => {
      const { [roomKey]: _removedRoomTab, ...nextTabs } = currentTabs;

      return nextTabs;
    });
  };

  const deleteRoom = async () => {
    if (!roomPendingDeletion) {
      return;
    }

    hasUserEditedRoomsRef.current = true;

    if (!roomPendingDeletion.id) {
      removeDraftRoom(roomPendingDeletion.roomKey);
      setRoomPendingDeletion(undefined);
      return;
    }

    if (isRoomAttachedToPackages(roomPendingDeletion, offerPackages)) {
      enqueueSnackbar(PACKAGE_ROOM_DELETE_WARNING, { variant: 'warning' });
      setRoomPendingDeletion(undefined);
      return;
    }

    try {
      setIsCheckingRoomUsage(true);

      let offersForUsageCheck = vendorOffers ?? [];

      if (vendorId && (!vendorOffers || vendorOffersError)) {
        const { data, error } = await refetchVendorOffers();

        if (error) {
          enqueueSnackbar('Unable to check whether this room is attached to an offer. Please try again.', {
            variant: 'error',
          });
          return;
        }

        offersForUsageCheck = data ?? [];
      }

      if (findOfferUsingRoom(roomPendingDeletion, offersForUsageCheck)) {
        enqueueSnackbar(PACKAGE_ROOM_DELETE_WARNING, { variant: 'warning' });
        setRoomPendingDeletion(undefined);
        return;
      }
    } finally {
      setIsCheckingRoomUsage(false);
    }

    const roomPropertyId = roomPendingDeletion.property_id ?? propertyId;

    if (!roomPropertyId) {
      enqueueSnackbar('Unable to delete room because the property ID is missing.', { variant: 'error' });
      return;
    }

    setDeletingRoomKey(roomPendingDeletion.roomKey);

    try {
      await deleteRoomMutation.mutateAsync({
        propertyId: roomPropertyId,
        roomId: roomPendingDeletion.id,
        roomName: roomPendingDeletion.name,
      });
      removeDraftRoom(roomPendingDeletion.roomKey);
      setRoomPendingDeletion(undefined);
    } finally {
      setDeletingRoomKey(undefined);
    }
  };

  const handleToggleAllRooms = () => {
    setExpandedRoomKeys(areAllRoomsExpanded ? [] : draftRoomTypes.map((room) => room.roomKey));
  };

  const handleExpandedChange = (roomKey: string, isExpanded: boolean) => {
    setExpandedRoomKeys((currentRoomKeys) =>
      isExpanded
        ? Array.from(new Set(currentRoomKeys.concat(roomKey)))
        : currentRoomKeys.filter((currentRoomKey) => currentRoomKey !== roomKey),
    );
  };

  const updateSavedRoom = (roomKey: string, savedRoom: SavedOfferBuilderRoom) => {
    setDraftRoomTypes((currentRoomTypes) =>
      currentRoomTypes.map((currentRoom) => {
        if (currentRoom.roomKey !== roomKey) {
          return currentRoom;
        }

        const nextRoom = {
          ...currentRoom,
          ...savedRoom,
          roomKey: currentRoom.roomKey,
        };

        return {
          ...nextRoom,
          roomSummary: getRoomSummary(nextRoom),
          roomTitle: nextRoom.name || 'Unnamed room',
        };
      }),
    );
  };

  const saveRoom = async (room: DraftRoom, matchingPackage?: App.Package, suppressNotifications = false) => {
    const roomPropertyId = room.property_id ?? propertyId;

    if (!roomPropertyId) {
      if (!suppressNotifications) {
        enqueueSnackbar('Unable to save room because the property ID is missing.', { variant: 'error' });
      }
      throw new Error('Missing property ID');
    }

    if (!suppressNotifications) {
      setSavingRoomKey(room.roomKey);
    }

    try {
      const savedRoom = await saveRoomMutation.mutateAsync({
        offerId,
        propertyId: roomPropertyId,
        room,
        roomPolicyId: room.roomPolicyId ?? getPackageRoomPolicyId(matchingPackage),
        suppressNotifications,
      });

      updateSavedRoom(room.roomKey, savedRoom);
      try {
        await media.saveRoomImageTitleEdits(room.roomKey);
      } catch (error) {
        if (!suppressNotifications) {
          enqueueSnackbar(getErrorMessage(error, 'Room saved but image descriptions failed to save'), {
            variant: 'warning',
          });
        }
        throw error;
      }
    } finally {
      if (!suppressNotifications) {
        setSavingRoomKey(undefined);
      }
    }
  };

  const saveAllRooms = async () => {
    setIsSavingAllRooms(true);
    setSaveAllRoomCount(0);
    setHasSaveAllRoomFailure(false);

    let savedRoomCount = 0;
    let failedRoomCount = 0;
    let skippedRoomCount = 0;

    try {
      for (const room of draftRoomTypes) {
        const matchingPackage = findMatchingPackageForRoom(room, offerPackages);

        try {
          if (getMissingRequiredRoomFields(room).length > 0) {
            skippedRoomCount += 1;
          } else {
            await saveRoom(room, matchingPackage, true);
            savedRoomCount += 1;
          }
        } catch {
          failedRoomCount += 1;
        } finally {
          setSaveAllRoomCount((currentCount) => currentCount + 1);
        }
      }
    } finally {
      setIsSavingAllRooms(false);
    }

    if (failedRoomCount > 0 || skippedRoomCount > 0) {
      setHasSaveAllRoomFailure(true);
      const saveAllResultMessage = [
        savedRoomCount > 0 && `${savedRoomCount} room${savedRoomCount === 1 ? '' : 's'} saved`,
        skippedRoomCount > 0 && `${skippedRoomCount} skipped`,
        failedRoomCount > 0 && `${failedRoomCount} failed`,
      ]
        .filter(Boolean)
        .join('. ');

      enqueueSnackbar(`${saveAllResultMessage}.`, { variant: 'warning' });
      return;
    }

    hasUserEditedRoomsRef.current = false;
    enqueueSnackbar(`${savedRoomCount} room(s) saved`, { variant: 'success' });
  };

  const saveAllButtonText = (() => {
    if (isSavingAllRooms) {
      return `Saving... ${saveAllRoomCount}/${draftRoomTypes.length}`;
    }

    if (!hasSaveAllRoomFailure && saveAllRoomCount === draftRoomTypes.length && draftRoomTypes.length > 0) {
      return 'All saved';
    }

    return 'Save all';
  })();

  const promptDeleteRoom = (room: DraftRoom) => {
    if (isRoomAttachedToPackages(room, offerPackages)) {
      enqueueSnackbar(PACKAGE_ROOM_DELETE_WARNING, { variant: 'warning' });
      return;
    }

    setRoomPendingDeletion(room);
  };

  if (propertyError) {
    return <Alert severity="error">Unable to load rooms for this property.</Alert>;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Rooms</Typography>
          <Typography variant="body1" color="text.secondary">
            Configure room copy, bedding, amenities, and gallery for each room type.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={handleToggleAllRooms} disabled={draftRoomTypes.length === 0}>
            {areAllRoomsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button variant="contained" size="small" onClick={() => setIsAddRoomDialogOpen(true)}>
            Add room
          </Button>
        </Stack>
      </Stack>

      <AddRoomDialog
        open={isAddRoomDialogOpen}
        propertyId={propertyId}
        onAdd={addRoom}
        onClose={() => setIsAddRoomDialogOpen(false)}
      />
      <DeleteConfirmationDialog
        body={`This will permanently delete ${
          roomPendingDeletion?.roomTitle || 'this room'
        } from the property in Reservation, not just from Offer Builder. It may affect other offers using this property. This action cannot be undone.`}
        confirmingText={isCheckingRoomUsage ? 'Checking...' : undefined}
        isConfirming={!!deletingRoomKey || isCheckingRoomUsage}
        open={!!roomPendingDeletion}
        title="Delete reservation room type?"
        onClose={() => {
          if (!deletingRoomKey && !isCheckingRoomUsage) {
            setRoomPendingDeletion(undefined);
          }
        }}
        onConfirm={() => deleteRoom().catch(() => undefined)}
      />

      <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 700 }}>
              {draftRoomTypes.length}
            </Box>{' '}
            rooms
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search rooms..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              disabled={isLoadingProperty || draftRoomTypes.length === 0}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={saveAllRooms}
              disabled={isSavingAllRooms || !!savingRoomKey || draftRoomTypes.length === 0}
            >
              {saveAllButtonText}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {draftRoomTypes.length === 0 && !isLoadingProperty && (
        <Alert severity="info">No rooms are currently available for this property.</Alert>
      )}

      {filteredRooms.length === 0 && draftRoomTypes.length > 0 && (
        <Alert severity="info">No rooms match the current search.</Alert>
      )}

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack spacing={2}>
          {filteredRooms.map((room) => {
            const roomKey = room.roomKey;
            const activeRoomTab = activeRoomTabs[roomKey] ?? DEFAULT_ROOM_TAB;
            const matchingPackage = findMatchingPackageForRoom(room, offerPackages);

            return (
              <RoomAccordion
                key={roomKey}
                activeRoomTab={activeRoomTab}
                expanded={expandedRoomKeys.includes(roomKey)}
                imageCount={getRoomImageCount(room)}
                media={media}
                onDelete={() => promptDeleteRoom(room)}
                onDuplicate={() => duplicateRoom(room)}
                onOpenImagesVideo={onOpenImagesVideo}
                onSave={() => saveRoom(room, matchingPackage).catch(() => undefined)}
                propertyName={propertyName}
                propertyRoomTypes={activeRoomTab === 'Images' ? draftRoomTypes : undefined}
                room={room}
                roomKey={roomKey}
                roomSummary={room.roomSummary}
                roomTitle={room.roomTitle}
                isDeleting={deletingRoomKey === roomKey}
                isSaving={isSavingAllRooms || savingRoomKey === roomKey}
                syncBeddingConfigurationPackageId={
                  matchingPackage ? getPackageSalesforceId(matchingPackage) : undefined
                }
                syncRoomPolicyParams={getSyncRoomPolicyParams(room, matchingPackage)}
                onChange={(nextRoomValues) => updateRoom(roomKey, nextRoomValues)}
                onExpandedChange={(isExpanded) => handleExpandedChange(roomKey, isExpanded)}
                onTabChange={(tab) => setActiveRoomTabs((currentTabs) => ({ ...currentTabs, [roomKey]: tab }))}
              />
            );
          })}
        </Stack>
      </Box>
      <ImageEditor
        edit={media.roomImagePendingEdit ? media.imageEdits[media.roomImagePendingEdit.publicId] : undefined}
        image={media.roomImagePendingEdit}
        open={!!media.roomImagePendingEdit}
        onClose={() => media.setRoomImagePendingEdit(undefined)}
        onDeleteImage={media.removeRoomImageFromEditor}
        onSave={media.saveImageEdit}
      />
      <DeleteConfirmationDialog
        body="This will remove the image from the room. This action cannot be undone."
        open={!!media.roomImagePendingDeletion}
        title="Delete image from room?"
        onClose={media.closeRoomImageDeleteDialog}
        onConfirm={media.confirmRemoveRoomImage}
      />
    </Box>
  );
}
